import { Component, ElementRef, Inject } from '@angular/core';
import { formatDate } from '@angular/common';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';

import { JobAction } from '../../../action.types';
import { LoginService } from '@intranet/modules/login';
import { UserService } from '@intranet/modules/users/user.service';
import { User } from '@intranet/modules/users/user.types';
import { AlertDialog } from '@intranet/shared/alert/alert-dialog.component';
import { TimerToastService } from '@intranet/modules/timer/timer-toast/timer-toast.service';
import { TimerSyncService } from '@intranet/core/services/timer-sync.service';
import { PlannedBlockService } from '../planned-block.service';
import { PlannedBlockViewModel } from '../planned-block.types';
import { PlannerCacheService } from '../planner-cache.service';
import { plannerColorForJobNumber } from '../planner-color.util';
import { formatDurationHM, formatTimeRange, parseDurationHM } from '../planner-date.util';
import { PlannerLogRange } from '../planner-log-timerow/planner-log-timerow.component';

export interface PlannedBlockDialogData {
    action: JobAction;
    block: PlannedBlockViewModel;
}

/** What the dialog closes with: whether the block was changed (moved,
 *  resized, removed or logged), so the list can refresh what it shows. */
export interface PlannedBlockDialogResult {
    changed: boolean;
}

// One of an action's planned blocks, opened from its ⋮ menu on the Actions
// list (spec: "a list of that Action's existing blocks, each opening the
// same edit popup as the planner"). The Planner's block popover lives on
// the calendar itself, so this is its counterpart for the list: the same
// fields and rules — day, start and length in 30-minute steps, Log as time,
// Remove — with the "Add to planner" dialog's look (its stylesheet is
// shared, see styleUrls). Only on planners the user may change (their own,
// or anyone's with the Planner role); anyone else's opens view-only.
@Component({
    selector: 'planned-block-dialog',
    templateUrl: './planned-block-dialog.component.html',
    styleUrls: [
        '../add-to-planner-dialog/add-to-planner-dialog.component.scss',
        './planned-block-dialog.component.scss',
    ],
})
export class PlannedBlockDialogComponent {
    private static readonly STEP_MINUTES = 30;
    private static readonly LAST_START_MINUTES = 24 * 60 - PlannedBlockDialogComponent.STEP_MINUTES; // 23:30

    readonly action: JobAction;
    readonly block: PlannedBlockViewModel;
    readonly personName: string;
    readonly readOnly: boolean;
    // Whose planner it's on — the Planner dropdown. With the Planner role,
    // it can go on another person's: the action's owners, whoever it's
    // waiting on, or the user's own (same people as "Add to planner").
    // Otherwise only the person it's on.
    readonly candidateUsers: User[];
    userId: string;
    readonly today: Date;
    isLogged: boolean;

    day: Date;
    dayPickerOpen = false;
    dayPickerUpward = false;
    startMinutes = 0;
    startHText = '00';
    startMText = '00';
    durationMinutes = 60;
    durationText = '1h';
    editingDuration = false;
    error = '';
    saving = false;
    private readonly _original: { day: string; start: number; duration: number };
    private _changed = false;

    // "Log as time" — the Planner's own form: a time entry pre-filled from
    // the block, editable before saving (free times, not the 30-min grid).
    logFormOpen = false;
    logDescription = '';
    logStart: Date;
    logEnd: Date;
    logError = '';
    logSaving = false;

    constructor(
        @Inject(MAT_DIALOG_DATA) data: PlannedBlockDialogData,
        private _dialogRef: MatDialogRef<PlannedBlockDialogComponent, PlannedBlockDialogResult>,
        private _dialog: MatDialog,
        private _plannedBlockService: PlannedBlockService,
        private _plannerCache: PlannerCacheService,
        private _loginService: LoginService,
        private _userService: UserService,
        private _timerToastService: TimerToastService,
        private _timerSyncService: TimerSyncService,
        private _el: ElementRef<HTMLElement>
    ) {
        this.action = data.action;
        this.block = data.block;
        this.isLogged = !!data.block.isLogged;
        const user = (this._userService.users || []).find((u) => u.id === data.block.userId);
        this.personName = user ? user.fullName || `${user.firstName} ${user.surname}` : '';
        this.readOnly = data.block.userId !== this._loginService.userId && !this._loginService.checkAuth(['Planner']);
        this.userId = data.block.userId;
        this.candidateUsers = this.buildCandidateUsers();

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        this.today = today;
        const start = new Date(data.block.start);
        const day = new Date(start);
        day.setHours(0, 0, 0, 0);
        this.day = day;
        this.setStart(start.getHours() * 60 + start.getMinutes());
        this.setDuration(data.block.durationMinutes);
        this._original = { day: this.day.toDateString(), start: this.startMinutes, duration: this.durationMinutes };
    }

    private buildCandidateUsers(): User[] {
        const users = this._userService.users || [];
        const me = this._loginService.userId;
        const current = this.block.userId;
        const ids = this.readOnly || !this._loginService.checkAuth(['Planner'])
            ? [current]
            : [
                current,
                ...(this.action.assignedUsers || []),
                ...(this.action.waitingUserId ? [this.action.waitingUserId] : []),
                ...(me ? [me] : []),
            ];
        const seen = new Set<string>();
        const result: User[] = [];
        ids.forEach((id) => {
            if (!id || seen.has(id)) return;
            seen.add(id);
            const user = users.find((u) => u.id === id);
            // Inactive people only if it's already on theirs.
            if (!user || (!user.active && id !== current)) return;
            result.push(user);
        });
        return result;
    }

    selectUser(userId: string | null): void {
        if (!userId) return; // the picker has no "clear" here
        this.userId = userId;
        this.error = '';
    }

    /** The only person it can go on, when that's who it's on: shown as a
     *  plain field instead of a one-option picker (singlePlanner). */
    get singlePlannerUser(): User | null {
        return this.candidateUsers.length === 1 && this.candidateUsers[0].id === this.userId
            ? this.candidateUsers[0]
            : null;
    }

    get jobDotColor(): string {
        return plannerColorForJobNumber(this.action.jobNumber).titleColor;
    }

    get actionTitle(): string {
        const t = this.action.title || this.block.jobActionTitle || '';
        return t.charAt(0).toUpperCase() + t.slice(1);
    }

    get hasChanges(): boolean {
        return this.day.toDateString() !== this._original.day
            || this.userId !== this.block.userId
            || this.startMinutes !== this._original.start
            || this.durationMinutes !== this._original.duration;
    }

    // ─── Day ──────────────────────────────────────────────────────────────

    toggleDayPicker(e: MouseEvent): void {
        if (this.readOnly) return;
        this.dayPickerOpen = !this.dayPickerOpen;
        if (!this.dayPickerOpen) return;
        const PICKER_H = 230;
        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
        this.dayPickerUpward = rect.bottom + PICKER_H > document.documentElement.clientHeight - 20;
    }

    onDayPicked(day: Date): void {
        this.day = day;
        this.dayPickerOpen = false;
    }

    // ─── Start ────────────────────────────────────────────────────────────

    private roundToStep(minutes: number): number {
        const step = PlannedBlockDialogComponent.STEP_MINUTES;
        return Math.round(minutes / step) * step;
    }

    private setStart(minutes: number): void {
        this.startMinutes = Math.min(PlannedBlockDialogComponent.LAST_START_MINUTES, Math.max(0, this.roundToStep(minutes)));
        this.startHText = String(Math.floor(this.startMinutes / 60)).padStart(2, '0');
        this.startMText = String(this.startMinutes % 60).padStart(2, '0');
    }

    commitStart(): void {
        const h = parseInt(this.startHText, 10);
        const m = parseInt(this.startMText || '0', 10);
        if (isNaN(h) || isNaN(m) || h < 0 || h > 23 || m < 0 || m > 59) {
            this.setStart(this.startMinutes);
            return;
        }
        this.setStart(h * 60 + m);
    }

    onSegInput(e: Event, seg: 'h' | 'm'): void {
        const input = e.target as HTMLInputElement;
        const digits = (input.value || '').replace(/\D/g, '').slice(0, 2);
        input.value = digits;
        if (seg === 'h') {
            this.startHText = digits;
            if (digits.length === 2) this.focusSeg('m');
        } else {
            this.startMText = digits;
        }
    }

    onSegKeydown(e: KeyboardEvent, seg: 'h' | 'm'): void {
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            this.commitStart();
            const step = seg === 'h' ? 60 : PlannedBlockDialogComponent.STEP_MINUTES;
            this.setStart(this.startMinutes + (e.key === 'ArrowUp' ? step : -step));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            this.save();
        }
    }

    onSegBlur(): void {
        setTimeout(() => {
            const active = document.activeElement as HTMLElement | null;
            if (active?.classList.contains('AddToPlanner-seg')) return;
            this.commitStart();
        }, 0);
    }

    private focusSeg(seg: 'h' | 'm'): void {
        const el = this._el.nativeElement.querySelector(`.AddToPlanner-seg[data-seg="${seg}"]`) as HTMLInputElement | null;
        el?.focus();
        el?.select();
    }

    // ─── Duration ─────────────────────────────────────────────────────────

    private setDuration(minutes: number): void {
        this.durationMinutes = Math.max(PlannedBlockDialogComponent.STEP_MINUTES, this.roundToStep(minutes));
        this.durationText = formatDurationHM(this.durationMinutes);
    }

    commitDuration(): void {
        this.setDuration(parseDurationHM(this.durationText) ?? this.durationMinutes);
    }

    startEditingDuration(): void {
        if (this.readOnly) return;
        this.editingDuration = true;
        setTimeout(() => {
            const el = this._el.nativeElement.querySelector('.AddToPlanner-durInput') as HTMLInputElement | null;
            el?.focus();
            el?.select();
        }, 0);
    }

    stopEditingDuration(): void {
        this.commitDuration();
        this.editingDuration = false;
    }

    onDurationKeydown(e: KeyboardEvent): void {
        const step = PlannedBlockDialogComponent.STEP_MINUTES;
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            this.commitDuration();
            this.setDuration(this.durationMinutes + (e.key === 'ArrowUp' ? step : -step));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            this.save();
        }
    }

    // ─── Save / remove / close ────────────────────────────────────────────

    close(): void {
        this._dialogRef.close({ changed: this._changed });
    }

    // Same save as the Planner's popover: the block's action and person
    // stay; its day, start and length change.
    save(): void {
        if (this.readOnly || this.saving) return;
        this.commitStart();
        this.commitDuration();
        if (!this.hasChanges) {
            this.close();
            return;
        }
        const start = new Date(this.day);
        start.setHours(0, this.startMinutes, 0, 0);
        // Moved to another person's planner: both planners change.
        const fromUserId = this.block.userId;
        const userId = this.userId;
        const invalidate = () => {
            this._plannerCache.invalidateBlocks(fromUserId);
            if (userId !== fromUserId) this._plannerCache.invalidateBlocks(userId);
        };
        this.saving = true;
        this.error = '';
        invalidate();
        this._plannedBlockService.updatePlannedBlock(this.block.plannedBlockId, {
            jobActionId: this.block.jobActionId,
            userId,
            start,
            durationMinutes: this.durationMinutes,
        }).subscribe({
            next: () => {
                invalidate();
                this._dialogRef.close({ changed: true });
            },
            error: () => {
                invalidate();
                this.saving = false;
                this.error = 'Could not save this block. Please try again.';
            },
        });
    }

    remove(): void {
        if (this.readOnly || this.saving) return;
        // Says which block (as saved — not any unsaved edits) and whose
        // planner it's on. Same size as the Planner's "Unplan" confirm.
        const start = new Date(this.block.start);
        const end = new Date(start.getTime() + this.block.durationMinutes * 60000);
        const when = `${formatDate(start, 'EEE d MMM', 'en-US')}, ${formatTimeRange(start, end)}`;
        const whose = this.block.userId === this._loginService.userId
            ? 'your planner'
            : `${this.personName || 'their'}${this.personName ? '’s' : ''} planner`;
        const confirmRef = this._dialog.open(AlertDialog, {
            width: '560px',
            maxWidth: '92vw',
            panelClass: 'PlannerFutureBlocksDialog', // spacing — see vendor/_material.scss
            data: {
                title: 'Remove planned block?',
                message: `This takes the block on ${when} off ${whose}. Any time already logged from it won’t be affected.`,
                confirm: true,
                confirmTitle: 'Remove',
                cancelTitle: 'Cancel',
            },
        });
        confirmRef.afterClosed().subscribe((confirmed) => {
            if (!confirmed) return;
            const userId = this.block.userId;
            this.saving = true;
            this._plannerCache.invalidateBlocks(userId);
            this._plannedBlockService.deletePlannedBlock(this.block.plannedBlockId).subscribe({
                next: () => {
                    this._plannerCache.invalidateBlocks(userId);
                    this._dialogRef.close({ changed: true });
                },
                error: () => {
                    this._plannerCache.invalidateBlocks(userId);
                    this.saving = false;
                    this.error = 'Could not remove this block. Please try again.';
                },
            });
        });
    }

    // ─── Log as time ──────────────────────────────────────────────────────

    openLogForm(): void {
        if (this.readOnly || this.isLogged) return;
        const start = new Date(this.block.start);
        this.logDescription = this.actionTitle;
        this.logStart = start;
        this.logEnd = new Date(start.getTime() + this.block.durationMinutes * 60000);
        this.logError = '';
        this.logSaving = false;
        this.logFormOpen = true;
    }

    cancelLogForm(): void {
        this.logFormOpen = false;
        this.logError = '';
    }

    onLogRangeChange(range: PlannerLogRange): void {
        this.logStart = range.start;
        this.logEnd = range.end;
    }

    submitLog(): void {
        if (this.logSaving) return;
        if (this.logEnd.getTime() <= this.logStart.getTime()) {
            this.logError = 'End time must be after start time.';
            return;
        }
        const description = this.logDescription.trim();
        this.logError = '';
        this.logSaving = true;
        this._plannedBlockService.logPlannedBlock(this.block.plannedBlockId, {
            startTime: this.logStart,
            endTime: this.logEnd,
            description: description.charAt(0).toUpperCase() + description.slice(1),
        }).subscribe({
            next: () => {
                this._plannerCache.invalidateBlocks(this.block.userId);
                // Same as the Planner: an open Timer picks the new entry up.
                this._timerSyncService.notify('entries-changed');
                this.isLogged = true;
                this._changed = true;
                this.logFormOpen = false;
                this.logSaving = false;
                this._timerToastService.show('Time logged.');
            },
            error: (err) => {
                this.logSaving = false;
                this.logError = (err instanceof Error && err.message) || 'Could not log this time. Please try again.';
            },
        });
    }
}
